import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { STANDARD_UNITS } from "@/lib/supplierHelpers";

export const ProductFormDialog = ({ open, onClose, supplier, product, onSaved }) => {
  const [form, setForm] = useState({
    reference: "", name: "", product_type: "", description: "", usage: "",
    order_type_ids: [], reference_unit: "kg", conversions: {},
    initial_price_amount: "", initial_price_quantity: "1", initial_price_unit: "",
  });
  const [orderTypes, setOrderTypes] = useState([]);
  const [newConvUnit, setNewConvUnit] = useState("");
  const [newConvRatio, setNewConvRatio] = useState("");
  const editing = !!product;

  useEffect(() => {
    api.getOrderTypes().then(r => setOrderTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({
        reference: product.reference || "",
        name: product.name || "",
        product_type: product.product_type || "",
        description: product.description || "",
        usage: product.usage || "",
        order_type_ids: product.order_type_ids || [],
        reference_unit: product.reference_unit || "kg",
        conversions: product.conversions || {},
        consumption_quantity: product.consumption_quantity ?? "",
        consumption_unit: product.consumption_unit || "",
        consumption_per_unit: product.consumption_per_unit || "",
        consumption_reference_thickness_mm: product.consumption_reference_thickness_mm ?? "",
        min_stock_threshold: product.min_stock_threshold ?? "",
        shipping_free_threshold: product.shipping_free_threshold ?? "",
        shipping_fee: product.shipping_fee ?? "",
        initial_price_amount: "", initial_price_quantity: "1", initial_price_unit: "",
      });
    } else {
      setForm({
        reference: "", name: "", product_type: "", description: "", usage: "",
        order_type_ids: [], reference_unit: "kg", conversions: {},
        consumption_quantity: "", consumption_unit: "", consumption_per_unit: "",
        consumption_reference_thickness_mm: "",
        min_stock_threshold: "",
        shipping_free_threshold: "", shipping_fee: "",
        initial_price_amount: "", initial_price_quantity: "1", initial_price_unit: "kg",
      });
    }
  }, [open, product]);

  const toggleOrderType = (id) => {
    setForm((f) => ({
      ...f,
      order_type_ids: f.order_type_ids.includes(id)
        ? f.order_type_ids.filter(x => x !== id)
        : [...f.order_type_ids, id],
    }));
  };

  const addConversion = () => {
    const unit = newConvUnit.trim();
    const ratio = parseFloat(newConvRatio);
    if (!unit) { toast.error("Unité requise"); return; }
    if (Number.isNaN(ratio) || ratio <= 0) { toast.error("Ratio invalide"); return; }
    setForm((f) => ({ ...f, conversions: { ...f.conversions, [unit]: ratio } }));
    setNewConvUnit(""); setNewConvRatio("");
  };

  const removeConversion = (unit) => {
    setForm((f) => {
      const c = { ...f.conversions };
      delete c[unit];
      return { ...f, conversions: c };
    });
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error("Nom du produit requis"); return; }
    if (!form.reference_unit) { toast.error("Unité de référence requise"); return; }
    try {
      const baseFields = {
        reference: form.reference || null,
        name: form.name,
        product_type: form.product_type || null,
        description: form.description || null,
        usage: form.usage || null,
        order_type_ids: form.order_type_ids,
        reference_unit: form.reference_unit,
        conversions: form.conversions,
        consumption_quantity: form.consumption_quantity === "" ? null : parseFloat(form.consumption_quantity),
        consumption_unit: form.consumption_unit || null,
        consumption_per_unit: form.consumption_per_unit || null,
        consumption_reference_thickness_mm: form.consumption_reference_thickness_mm === "" ? null : parseFloat(form.consumption_reference_thickness_mm),
        min_stock_threshold: form.min_stock_threshold === "" ? null : parseFloat(form.min_stock_threshold),
        shipping_free_threshold: form.shipping_free_threshold === "" ? null : parseFloat(form.shipping_free_threshold),
        shipping_fee: form.shipping_fee === "" ? null : parseFloat(form.shipping_fee),
      };
      if (editing) {
        await api.updateSupplierProduct(product.id, baseFields);
        toast.success("Produit mis à jour");
      } else {
        const payload = { supplier_id: supplier.id, ...baseFields };
        if (form.initial_price_amount) {
          payload.initial_price_amount = parseFloat(form.initial_price_amount);
          payload.initial_price_quantity = parseFloat(form.initial_price_quantity) || 1;
          payload.initial_price_unit = form.initial_price_unit || form.reference_unit;
        }
        await api.createSupplierProduct(payload);
        toast.success("Produit créé");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          <DialogDescription>Fiche produit : références, usage, unité de comparaison et équivalences.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Référence</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="product-reference" />
            </div>
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
            </div>
            <div>
              <Label>Type de produit</Label>
              <Input
                value={form.product_type}
                onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                placeholder="Peinture, Colle, Visserie..."
                data-testid="product-type"
              />
            </div>
            <div>
              <Label>Unité de référence *</Label>
              <Select value={form.reference_unit} onValueChange={(v) => setForm({ ...form, reference_unit: v })}>
                <SelectTrigger data-testid="product-reference-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STANDARD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Prix ramenés automatiquement à cette unité pour comparer.</p>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>Usage</Label>
            <Textarea rows={2} value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })}
              placeholder="Ex: application rouleau, sols intérieurs..."
            />
          </div>
          <div>
            <Label>Destiné aux types de commandes</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {orderTypes.length === 0
                ? <p className="text-xs text-muted-foreground">Aucun type de commande configuré.</p>
                : orderTypes.map(ot => {
                  const active = form.order_type_ids.includes(ot.id);
                  return (
                    <button
                      type="button"
                      key={ot.id}
                      onClick={() => toggleOrderType(ot.id)}
                      className={`text-xs px-2 py-1 rounded-full border transition ${active ? "bg-primary text-primary-foreground border-primary" : "bg-muted hover:bg-muted/60"}`}
                      data-testid={`product-ordertype-${ot.id}`}
                    >
                      {ot.name}
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="border rounded p-3 bg-muted/20 space-y-2">
            <div>
              <Label>Équivalences (pour comparaison de prix)</Label>
              <p className="text-xs text-muted-foreground">
                Ex: 1 bidon = 25 {form.reference_unit} (contient 25 {form.reference_unit} de produit). 1 L = 1,2 kg si densité = 1,2.
              </p>
            </div>
            {Object.keys(form.conversions).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(form.conversions).map(([u, r]) => (
                  <Badge key={u} variant="outline" className="flex items-center gap-1" data-testid={`conversion-${u}`}>
                    1 {u} = {r} {form.reference_unit}
                    <button onClick={() => removeConversion(u)} className="hover:text-red-500" type="button">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="grid grid-cols-5 gap-2 items-end">
              <div className="col-span-2">
                <Label className="text-xs">Unité</Label>
                <Select value={newConvUnit} onValueChange={setNewConvUnit}>
                  <SelectTrigger data-testid="new-conv-unit" className="h-8"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {STANDARD_UNITS.filter(u => u !== form.reference_unit).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">= ? {form.reference_unit}</Label>
                <Input
                  type="number" step="0.0001" value={newConvRatio}
                  onChange={(e) => setNewConvRatio(e.target.value)}
                  className="h-8" data-testid="new-conv-ratio"
                />
              </div>
              <Button size="sm" onClick={addConversion} data-testid="add-conversion-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="border rounded p-3 bg-indigo-50/40 space-y-2">
            <div>
              <Label>Consommation (pour calculer le prix au m², m, etc.)</Label>
              <p className="text-xs text-muted-foreground">
                Ex : « 0,1 L de produit pour couvrir 1 m² » → permet de comparer le coût au m² entre produits.
              </p>
            </div>
            <div className="grid grid-cols-4 gap-2 items-end">
              <div>
                <Label className="text-xs">Quantité</Label>
                <Input
                  type="number" step="0.0001" value={form.consumption_quantity}
                  onChange={(e) => setForm({ ...form, consumption_quantity: e.target.value })}
                  data-testid="consumption-quantity" className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Unité</Label>
                <Select value={form.consumption_unit} onValueChange={(v) => setForm({ ...form, consumption_unit: v })}>
                  <SelectTrigger data-testid="consumption-unit" className="h-8"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    {STANDARD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">par (m², m, pièce...)</Label>
                <Select value={form.consumption_per_unit} onValueChange={(v) => setForm({ ...form, consumption_per_unit: v })}>
                  <SelectTrigger data-testid="consumption-per-unit" className="h-8"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    {["m²", "m", "pièce", "unité", "m³"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Épaisseur de référence (mm) — optionnel</Label>
              <Input
                type="number" step="0.1" min="0"
                value={form.consumption_reference_thickness_mm}
                onChange={(e) => setForm({ ...form, consumption_reference_thickness_mm: e.target.value })}
                placeholder="Ex : 50 — laissez vide si le produit ne dépend pas de l'épaisseur"
                className="h-8" data-testid="consumption-thickness"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si renseignée, le coût sera recalculé proportionnellement à l'épaisseur demandée sur la commande
                (ex : 50 mm → 100 mm = coût × 2).
              </p>
            </div>
            <div>
              <Label className="text-xs">Seuil de stock minimum ({form.reference_unit}) — optionnel</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.min_stock_threshold}
                onChange={(e) => setForm({ ...form, min_stock_threshold: e.target.value })}
                placeholder="Ex : 50 — l'alerte se déclenchera avant rupture"
                className="h-8" data-testid="min-stock-threshold"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si renseigné, l'onglet "Stock & Prévisionnel" alerte dès que le stock projeté
                (actuel − consommation prévue) passe sous ce seuil.
              </p>
            </div>
            <div className="border rounded p-3 bg-blue-50/40 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Franco de port à partir de (€ HT)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.shipping_free_threshold}
                  onChange={(e) => setForm({ ...form, shipping_free_threshold: e.target.value })}
                  placeholder="Ex : 500"
                  className="h-8" data-testid="shipping-free-threshold"
                />
              </div>
              <div>
                <Label className="text-xs">Frais de port si en dessous (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.shipping_fee}
                  onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })}
                  placeholder="Ex : 50"
                  className="h-8" data-testid="shipping-fee"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 col-span-2">
                Utilisé dans la comparaison de produits pour afficher le coût réel
                (matière + port) et faciliter le choix entre plusieurs fournisseurs.
              </p>
            </div>
          </div>

          {!editing && (
            <div className="border rounded p-3 bg-sky-50/40 space-y-2">
              <Label>Prix initial (optionnel)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Quantité</Label>
                  <Input
                    type="number" step="0.01" value={form.initial_price_quantity}
                    onChange={(e) => setForm({ ...form, initial_price_quantity: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Unité</Label>
                  <Select value={form.initial_price_unit} onValueChange={(v) => setForm({ ...form, initial_price_unit: v })}>
                    <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent>
                      {STANDARD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Montant (€)</Label>
                  <Input
                    type="number" step="0.01" value={form.initial_price_amount}
                    onChange={(e) => setForm({ ...form, initial_price_amount: e.target.value })}
                    data-testid="initial-price-amount"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} data-testid="save-product-btn">{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
